import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  ArrowRightIcon,
  CalendarCheckIcon,
  FlameIcon,
  MoonIcon,
  SunriseIcon,
  XIcon,
} from "lucide-react";
import { addDays } from "../../lib/goal-stats";
import {
  computeStreakWithToday,
  deriveTimeline,
  greetingForHour,
  localDateString,
  planWeekKey,
} from "./utils";
import { DayProgress } from "./components/DayProgress";
import { DayScoreRing } from "./components/DayScoreRing";
import { DoneSection } from "./components/DoneSection";
import { MoodCheckIn } from "./components/MoodCheckIn";
import { AllDoneCard, NowCard } from "./components/NowCard";
import { RecoverSection } from "./components/RecoverSection";
import { SharedGoalsToday } from "./components/SharedGoalsToday";
import { UpNextSection } from "./components/UpNextSection";
import { useDashboardData } from "./hooks/useDashboardData";
import { useNow } from "./hooks/useNow";

export function DashboardPage() {
  const queryClient = useQueryClient();
  const now = useNow();
  const {
    todayDashboard: { data, isPending, isError },
    me: { data: me },
    todayEntry: { data: todayEntry },
    yesterdayEntries: { data: yesterdayEntries },
    historyResponse: { data: historyResponse },
  } = useDashboardData();

  const historyData = historyResponse?.days;

  // Day rollover: if the tab stays open past midnight, refetch today's dashboard.
  useEffect(() => {
    if (data && localDateString(now) !== data.localDate) {
      void queryClient.invalidateQueries({ queryKey: ["today-dashboard"] });
    }
  }, [now, data, queryClient]);

  if (isPending) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-7 h-7 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <p className="text-sm text-muted-foreground">Loading your day…</p>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-destructive text-sm">Failed to load dashboard.</p>
      </div>
    );
  }

  const formattedDate = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(new Date(data.localDate + "T00:00:00"));

  const timeline = deriveTimeline(data);

  const streak = historyData
    ? computeStreakWithToday(historyData, data.localDate, data.completedCount)
    : 0;

  const last7Dates = Array.from({ length: 7 }, (_, i) => addDays(data.localDate, i - 6));
  const historyByDate = new Map(
    historyData?.map((d) => [d.localDate, d.completionPercent]) ?? [],
  );
  historyByDate.set(data.localDate, data.completionPercent);

  const firstName = me?.name?.trim().split(/\s+/)[0];
  const greeting = greetingForHour(now.getHours());

  const hasGoals = data.goals.length > 0;
  const dayFinished = hasGoals && !timeline.hero && timeline.recover.length === 0;
  const skippedOnly =
    dayFinished && timeline.done.every((i) => i.slot.status === "SKIPPED");
  // Misses are the only thing left: recover takes the hero position.
  const recoverLeads = !timeline.hero && timeline.recover.length > 0;

  // Yesterday's pre-committed first task leads the morning until something is done.
  const firstTask = yesterdayEntries?.[0]?.tomorrowFirstTask ?? null;
  const showFirstTask = !!firstTask && data.completedCount === 0 && !dayFinished;

  // Evening shutdown entry: from 20:00, while there is anything left to resolve
  // or the day has not been formally closed.
  const hasUnresolved = !!timeline.hero || timeline.recover.length > 0;
  const showShutdown =
    hasGoals &&
    now.getHours() >= 20 &&
    (hasUnresolved || !(todayEntry?.entry?.shutdownNoteDone ?? false));

  return (
    <div className="relative mx-auto w-full max-w-2xl lg:max-w-6xl px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      {/* Ambient breathing backdrop */}
      <div aria-hidden className="breathing-orb" />

      {/* Header */}
      <header className="mb-5 lg:mb-8 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div>
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-widest mb-0.5">
            {formattedDate}
          </p>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight">
            {firstName && !firstName.includes("@") ? `${greeting}, ${firstName}` : greeting}
          </h1>
        </div>
        {hasGoals && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium tabular-nums text-muted-foreground">
              {data.completedCount}/{data.totalCount} done
            </span>
            {streak >= 2 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 text-xs font-medium tabular-nums text-primary">
                <FlameIcon className="w-3.5 h-3.5" />
                {streak}-day streak
              </span>
            )}
          </div>
        )}
      </header>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(300px,360px)] lg:gap-8 lg:items-start">
        {/* Timeline — what to do now */}
        <div className="min-w-0 space-y-5">
          {!hasGoals ? (
            <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              No goals for today.{" "}
              <Link to="/goals" className="text-primary font-medium hover:underline">
                Add some in Goals
              </Link>
              .
            </div>
          ) : (
            <>
              {showFirstTask && (
                <p className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
                  <SunriseIcon className="w-3.5 h-3.5 shrink-0 text-primary" />
                  First: {firstTask}
                </p>
              )}
              {timeline.hero && (
                <NowCard
                  item={timeline.hero}
                  next={timeline.upNext[0] ?? null}
                  now={now}
                />
              )}
              {dayFinished && <AllDoneCard skippedOnly={skippedOnly} />}

              <RecoverSection items={timeline.recover} lead={recoverLeads} />
              <UpNextSection items={timeline.upNext} />
              <DoneSection items={timeline.done} defaultOpen={dayFinished} />

              {showShutdown && <ShutdownCard />}
            </>
          )}

          <PlanCard now={now} localDate={data.localDate} />
        </div>

        {/* Insights — how the day is going. One column on phones, two-up on
            tablets, a dedicated side rail on laptops and up. */}
        <aside className="min-w-0 grid gap-5 sm:grid-cols-2 lg:grid-cols-1">
          <DayScoreRing
            goalCompletionPercent={hasGoals ? data.completionPercent : null}
            entry={todayEntry?.entry ?? null}
          />

          <DayProgress
            completionPercent={data.completionPercent}
            completedCount={data.completedCount}
            totalCount={data.totalCount}
            last7Dates={last7Dates}
            percentByDate={historyByDate}
            todayDate={data.localDate}
            streak={streak}
          />

          <SharedGoalsToday />

          <MoodCheckIn />
        </aside>
      </div>
    </div>
  );
}

function ShutdownCard() {
  return (
    <Link
      to="/shutdown"
      className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-muted/30 px-4 py-3 hover:bg-muted/50 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <MoonIcon className="w-4 h-4 shrink-0 text-primary" />
        <div className="min-w-0">
          <p className="text-sm font-medium">Shut down the day</p>
          <p className="text-xs text-muted-foreground truncate">
            Close out what's left and decide tomorrow's first task.
          </p>
        </div>
      </div>
      <ArrowRightIcon className="w-4 h-4 shrink-0 text-muted-foreground" />
    </Link>
  );
}

function PlanCard({ now, localDate }: { now: Date; localDate: string }) {
  const weekKey = planWeekKey(localDate);
  const storageKey = `plan-card-dismissed-${weekKey}`;
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(storageKey) === "true",
  );

  const isWeekend = now.getDay() === 0 || now.getDay() === 6;
  if (!isWeekend || dismissed) return null;

  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-muted/30 px-4 py-3">
      <Link to="/plan" className="flex items-center gap-3 min-w-0 flex-1 group">
        <CalendarCheckIcon className="w-4 h-4 shrink-0 text-primary" />
        <div className="min-w-0">
          <p className="text-sm font-medium group-hover:text-primary transition-colors">
            Plan next week
          </p>
          <p className="text-xs text-muted-foreground truncate">
            Keep, adjust, or pause each goal — two minutes, then it's decided.
          </p>
        </div>
      </Link>
      <button
        onClick={() => {
          localStorage.setItem(storageKey, "true");
          setDismissed(true);
        }}
        aria-label="Dismiss planning reminder for this week"
        className="shrink-0 p-1.5 rounded-lg text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted transition-colors"
      >
        <XIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
